import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Menu } from 'primereact/menu';
import { utils, write } from "xlsx";
import Papa from "papaparse";

import { DropDownField, TextInputField, AutoCompleteField, MultiSelectField } from "../../../helpers";
import Button from "../../../helpers/Button";
import DateFilter from "../../../common/DateFilter";

import { getBuyerPOs } from "../../../services/api/buyerPO";
import { raiseToast } from "../../../services/redux/reducers/toast/toast";
import { fetchBuyerPOs, updateSearchOptions, updateSearchParams, setSearchConfig } from "../../../services/redux/reducers/buyerPO/buyerPO";
import { fetchBuyerCompanies, fetchBuyerCompaniesUsers } from "../../../services/redux/reducers/commonThunk";
import { getAddFilterOptions, getAutoSearchSuggestions, getDateRange, getFullName, getMatchedRegexPattern, normalizeLabel, useFuzzSearch } from "../../../utils";


const vpCreditNonCreditConfig = [
    {
        srNO: 1,
        static: true,
        type: "SELECT",
        name: "dataType",
        label: "Data type",
        options: [
            { label: 'Buyer PO Data', value: 'buyerPOsData' }
        ]
    },
    {   srNO: 2,
        static: true,
        type: "SEARCH_TEXT",
        name: "searchText",
        label: "Search Text",  
    },
    {
        srNO: 3,
        static: true,
        type: "DATE",
        name: "dateType",
        label: "Date",
        options: [
            { label: 'Created Date', value: 'createdAt' }
        ]
    },
    {
        srNO: 4,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "docNo",
        label:"Doc No",
    },
    {
        srNO: 5,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "fileNo",
        label:"File No",
    },
    {
        srNO: 6,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "AUTO_COMPLETE",
        name: "buyerName",
        label:"Buyer Name",
    },
    {
        srNO: 7,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "AUTO_COMPLETE",
        name: "kamName",
        label:"Kam Name",
        priority: 2
    },
    {
        srNO: 8,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "AUTO_COMPLETE",
        name: "companyName",
        label:"Company Name",
        priority: 1
    },
    {
        srNO: 9,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "clientPONo",
        label:"Client PO No",
    },
    {
        srNO: 10,
        static: false,
        type: "MULTISELECT",
        name: "reqTagId",
        label: "Req Tags",
        options: [
            { label: 'Corporate Solutions', value: 2 },
            { label: 'Projects', value: 1 }
        ]
    },
    {
        srNO: 11,
        static: false,
        type: "SELECT",
        name: "isRouted",
        label: "Routed",
        options: [
            { label: 'YES', value: "ROUTED" },
            { label: 'NO', value: "NOT_ROUTED" }
        ]
    },
    {
        srNO: 12,
        static: false,
        type: "SELECT",
        name: "orderTypeId",
        label: "Order Type",
        options: [
            { label: 'Adhoc', value: 1 },
            { label: 'Rate Contract', value: 2 },
            { label: 'Stock', value: 3 }
        ]
    },
]

const Search = (props) => {
    const [suggestions, setSuggestions] = useState([])

    const usersData = useSelector(state => state.userDetails)
    const searchParams = useSelector(state => state.buyerPOData.searchParams)
    const paginationParams = useSelector(state => state.buyerPOData.paginationParams)
    const searchOptions = useSelector(state => state.buyerPOData.searchOptions)
    const companyBranches = usersData?.PMCompanyDetails?.companyBranches
    const isPageLoading = useSelector(state => state.buyerPOData.loading)
    const userDetails = useSelector(state => state.userDetails)

    const searchConfig = vpCreditNonCreditConfig

    const filterMenu = useRef(null)
    const searchTextRef = useRef(null)

    const dispatch = useDispatch()

    let pmCompanyUsers = userDetails.PMCompanyDetails.companyUsers ? userDetails.PMCompanyDetails.companyUsers : []
    let buyerCompaniesUsers = useSelector(state => state.common.buyerCompaniesUsers)
    let buyerCompanies = useSelector(state => state.common.buyerCompanies)

    pmCompanyUsers = pmCompanyUsers.map(eachPerson => {
        const name = getFullName(eachPerson.firstName,eachPerson.lastName)
        return {...eachPerson, displayName: name}
    })

    buyerCompaniesUsers = buyerCompaniesUsers.map(eachPerson => {
        const buyername = getFullName(eachPerson.firstName,eachPerson.lastName)
        return {...eachPerson, displayName: buyername}
    })

    if (buyerCompanies && buyerCompanies.length){
        buyerCompanies = buyerCompanies.map(company => {
            const companyName = company.companyName 
            return {...company, displayName: companyName}
        })
    }

    useEffect(()=>{
        dispatch(setSearchConfig(vpCreditNonCreditConfig))
        dispatch(fetchBuyerCompaniesUsers())
        dispatch(fetchBuyerCompanies())

        if(searchOptions.length === 0){
            const filteredSearchOptions = searchConfig.filter(eachOption => eachOption.static)
            dispatch(updateSearchOptions(filteredSearchOptions))
        }
    }, [])

    useEffect(() => {
        if (searchTextRef.current) {
            searchTextRef.current.focus()
        }
    }, [searchParams.searchBy])

    useEffect(() => {
        if (['kamName', 'buyerName', 'companyName'].includes(searchParams.searchBy)) {
            autoCompleteSearch(searchParams.searchBy, searchParams.searchText)
        }
    }, [searchParams.searchBy])

    const kamFuzzSearch = useFuzzSearch(pmCompanyUsers, "displayName")
    const buyerNameFuzzSearch = useFuzzSearch(buyerCompaniesUsers, "displayName")
    const companyFuzzSearch = useFuzzSearch(buyerCompanies, "displayName")
    
    const addFilterOption = (srNO) => {
        const relatedConfig = searchConfig.find(eachConfig => eachConfig.srNO === srNO)
        if(relatedConfig.name === "branchId"){
            const updatedCompanyBranches = companyBranches?.map(eachCompanyBranch => ({...eachCompanyBranch, label:eachCompanyBranch.branchName, value:eachCompanyBranch.branchId}))
            relatedConfig.options = updatedCompanyBranches
        }
        dispatch(updateSearchOptions([...searchOptions, relatedConfig]))
    }

    const removeFilterOption = (srNO) => {
        const relatedSearchOption = searchOptions.find(eachOption => eachOption.srNO === srNO)
        const updatedSearchParams = {...searchParams}

        if(relatedSearchOption.type === "SEARCH_BY"){
            updatedSearchParams.searchText = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if(relatedSearchOption.type === "DATE"){
            updatedSearchParams.fromDate = null
            updatedSearchParams.toDate = null
            updatedSearchParams.range = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if(relatedSearchOption.type === "SELECT"){
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "TEXT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "MULTISELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "AUTO_COMPLETE") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }

        const filteredSearchOptions = searchOptions.filter(eachOption => eachOption.srNO !== srNO)
        dispatch(updateSearchOptions([...filteredSearchOptions]))
    }

    const handleSearchOnChange = (event) => {
        let eventValue = event.target.value

        if(eventValue && (eventValue.userId || eventValue.companyId)){
            eventValue = eventValue.displayName
        }

        const updatedSearchParams = {...searchParams, [event.target.name]: eventValue}

        if (eventValue instanceof Date) {
            if(new Date(searchParams[event.target.name])?.getTime() != eventValue.getTime()){
                updatedSearchParams.range = null
            }

            if (event.target.name === "toDate") {
                eventValue.setHours(23, 59, 0, 0)
            }
            if (event.target.name === "fromDate") {
                eventValue.setHours(0, 0, 0, 0)
            }
            eventValue = eventValue.toISOString()

            updatedSearchParams[event.target.name] = eventValue
        }

        if(event.target.name === "searchText"){
            const availableOptions = [...vpCreditNonCreditConfig]
            const matchText = eventValue.split(";")[0]
            const matchedPattern = getMatchedRegexPattern(matchText, availableOptions, searchParams.searchBy)
            updatedSearchParams.searchBy = matchedPattern
        }
        
        dispatch(updateSearchParams(updatedSearchParams))
    }

    const handleDateRangeChange = (range) => {
        const { fromDate, toDate } = getDateRange(range)
        getDateRange(range)
        dispatch(updateSearchParams({ ...searchParams, range, fromDate, toDate }))
    }

    const autoCompleteSearch = (searchType, query) => {        
        const suggestions = getAutoSearchSuggestions({
            searchType,
            query,
            pmCompanyUsers,
            buyerCompaniesUsers,
            buyerCompanies,
            kamFuzzSearch,
            buyerNameFuzzSearch,
            companyFuzzSearch
        })
        
        setSuggestions(suggestions)
    }

    const addFilterOptions = getAddFilterOptions(searchConfig, searchOptions, searchParams, addFilterOption)

    const downloadData = async (dataFormat) => {
        let response = await getBuyerPOs({...searchParams, paginator: false})
        // const response = await fetchSellerPOs({ ...searchParams, paginator: false })
        const responseData = response?.data?.data
        // console.log("responseData", responseData)
        if (dataFormat === "CSV") {
            let csv = Papa.unparse(responseData, { download: true })
            let blob = new Blob([csv])
            let a = window.document.createElement("a")
            a.href = window.URL.createObjectURL(blob, { type: "text/plain" })
            a.download = "buyerPOs.csv"
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
        }
        if (dataFormat === "XLSX") {
            const ws = utils.json_to_sheet(responseData)
            const wb = utils.book_new()
            utils.book_append_sheet(wb, ws, 'Sheet1')
            const blob = new Blob([write(wb, { bookType: 'xlsx', type: 'array' })])
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'buyerPOs.xlsx'
            a.click()
            URL.revokeObjectURL(url)
        }
    }

    const downloadFormatOptions = [
        {
            label: "XLSX",
            command: () => downloadData("XLSX")
        },
        {
            label: "CSV",
            command: () => downloadData("CSV")
        }
    ]

    const handleSearch = () => {
        let errorFound = false

        if (searchParams.searchText === "" && searchParams.searchBy !== "all") {
            dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: "Please enter search text" }))
            errorFound = true
        } 
        else if (searchParams.searchText && searchParams.searchBy !== "all"){
            const relatedSearchOption = searchConfig.find(eachOption => eachOption.name === searchParams.searchBy)
            if(relatedSearchOption.isValid && !relatedSearchOption.isValid(searchParams.searchText.split(";")[0])){
                dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: `Please enter valid ${normalizeLabel(searchParams.searchBy).toLowerCase()}` }))
                errorFound = true
            }
        }
        
        searchOptions.map(eachOption => { 
            if (["TEXT", "SELECT", "AUTO_COMPLETE"].includes(eachOption.type)) {
                if (!searchParams[eachOption.name] && searchParams[eachOption.name] !== false) {
                    const errorMsgText = normalizeLabel(eachOption.label)
    
                    dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter ${errorMsgText.toLowerCase()}` }))
                    errorFound = true
                } else {
                    if(eachOption.isValid && !eachOption.isValid(searchParams[eachOption.name].split(";")[0])){
                        const errorMsgText = normalizeLabel(eachOption.label)
                        dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter valid ${errorMsgText.toLowerCase()}` }))
                        errorFound = true
                    }
                }
            } 
        })

        const updatedSearchParams = {...searchParams}

        if(updatedSearchParams.searchBy !== "all"){
            updatedSearchParams[updatedSearchParams.searchBy] = updatedSearchParams.searchText.trim()
        }

        if(!errorFound){
            dispatch(fetchBuyerPOs({...paginationParams, ...updatedSearchParams, pageNo: 0 }))
        }
    
        // dispatch(fetchBuyerPOs({...paginationParams, ...searchParams, pageNo: 0}))
        props.setSelectedBuyerPOs([])
    }

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch()
        }
    }
    
    return (
        <div className="p-card" style={{border:"1px solid #e9ecef"}} onKeyDown={handleSearchKeyPress} >
            <div className="grid"> 
                {
                    searchOptions.map(eachOption => {
                        if (eachOption.type === "SEARCH_TEXT") {

                            let searchByOptions = [{label: 'No Filters', value: 'all'}]
                            searchConfig.map(eachSearchBy => {
                                if (eachSearchBy.isSearchByOption) {
                                    const optionInUse = searchOptions.find(eachSearchOption => eachSearchOption.srNO === eachSearchBy.srNO)
                                    if(!optionInUse){
                                        searchByOptions.push({ label: eachSearchBy.label, value: eachSearchBy.name })
                                    }
                                }
                            })
    
                            return (
                                <>
                                    <div className="flex flex-column col-12">
                                        {['kamName', 'buyerName', 'companyName'].includes(searchParams.searchBy) ? (
                                            <AutoCompleteField
                                                value={searchParams.searchText}
                                                suggestions={suggestions}
                                                searchMethod={(e) => autoCompleteSearch(searchParams.searchBy, e.query)}
                                                field="displayName"
                                                labelText="Search Text"
                                                onChange={handleSearchOnChange}
                                                name="searchText"
                                                dropdown
                                                refName={searchTextRef}
                                            />
                                        ) : (
                                            <TextInputField
                                                name="searchText"
                                                labelText="Search Text"
                                                placeholder="No Filter"
                                                refName={searchTextRef}
                                                value={searchParams["searchText"]}
                                                onChange={handleSearchOnChange}
                                            />
                                        )}
                                    </div>
                                    {searchParams.searchText && (
                                        <div className="flex flex-column col-12">
                                            <DropDownField
                                                name="searchBy"
                                                labelText="Search By"
                                                options={searchByOptions}
                                                value={searchParams["searchBy"]}
                                                onChange={handleSearchOnChange}
                                            />
                                        </div>
                                    )}
                                </>
                            )
                        }

                        if(eachOption.type === "DATE"){
                            return (
                                <>
                                    <DateFilter 
                                        eachOption={eachOption} 
                                        searchParams={searchParams} 
                                        dateOptions={["MTD", "PM", "L15", "L30"]}
                                        handleDateRangeChange={handleDateRangeChange} 
                                        handleSearchOnChange={handleSearchOnChange}
                                        removeFilterOption={removeFilterOption}
                                    />
                                </>
                            )
                        }

                        if(eachOption.type === "SELECT"){
                            return (
                                <>
                                    { !eachOption.static ? 
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={()=> removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                        :
                                        null 
                                    }
                                    <div className="flex flex-column col-12">
                                        <DropDownField name={eachOption.name} options={eachOption.options} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }

                        if (eachOption.type === "MULTISELECT") {
                            return (
                                <>
                                    {!eachOption.static ?
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                        :
                                        null
                                    }
                                    <div className="flex flex-column col-12 custom-multiselect">
                                        <MultiSelectField name={eachOption.name} labelText={eachOption.label} options={eachOption.options} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }

                        if (eachOption.type === "TEXT") {
                            return (
                                <>
                                    {!eachOption.static &&
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                    }
                                    {
                                        <div className="flex flex-column col-12">
                                            <TextInputField name={eachOption.name} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                        </div>
                                    }
                                </>
                            )
                        }
    
                        if (eachOption.type === "AUTO_COMPLETE") {
                            return (
                                <>
                                    {!eachOption.static &&
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                    }
                                    {
                                        <div className="flex flex-column col-12">
                                            <AutoCompleteField
                                                value={searchParams[eachOption.name]}
                                                suggestions={suggestions}
                                                searchMethod={(e) => autoCompleteSearch(eachOption.name, e.query)}
                                                field="displayName"
                                                labelText={eachOption.label}
                                                onChange={handleSearchOnChange}
                                                name={eachOption.name}
                                                dropdown
                                            />
                                        </div>
                                    }
                                </>
                            )
                        }
                    })
                }
                <div className="flex col-12 justify-content-end">
                    <Menu model={addFilterOptions} popup ref={filterMenu} />
                    <Button icon="pi pi-filter" className="h-2rem p-2 action-btn hover:surface-300 hover:text-700" label="Add Filter" onClick={(e) => filterMenu.current.toggle(e)} />
                </div>

                <div className="flex flex-column col-12 mb-0 pb-0">
                    <div className="grid">
                        <div className="flex col-12 mb-0 gap-1 pb-0" style={{cursor : isPageLoading ? 'not-allowed' : 'pointer'}}>
                            <Button label="Search" style={{flexGrow: 1}} className="py-2" onClick={handleSearch} disabled={isPageLoading} />
                            
                            {/* <Menu model={downloadFormatOptions} popup ref={downloadMenu} />
                            <Button icon="pi pi-download" className="py-2 px-3"  onClick={(e) => downloadMenu.current.toggle(e)} /> */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Search;